import ChevronLeft from '@mui/icons-material/ChevronLeft';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ClockIcon } from '@mui/x-date-pickers';
import { format } from 'date-fns/format';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getOrderSummary } from '../../../api/customerOrders';
import { handleError } from '../../../api/requests';
import { isStaff } from '../../../common/typeAssertionFunctions';
import {
  OrderSummary
} from '../../../common/types';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import OrderItemsList from '../../../components/orderSummary/OrderItemsList';
import OrderPricingList from '../../../components/orderSummary/OrderPricingList';
import useAuthStore from '../../../datastore/useAuth';
import useLoyaltyPoints from '../../../datastore/useLoyaltyPoints';
import useAlert from '../../../hooks/useAlert';
import useRequest from '../../../hooks/useRequest';


export default function OrderConfirmed() {
  const navigate = useNavigate()
  const theme = useTheme();
  const { orderId } = useParams();
  const { data, loading, error } = useRequest<OrderSummary>(
    () => getOrderSummary(+orderId!),
    [orderId],
  );
  const { fetchLoyaltyPointsForCustomer } = useLoyaltyPoints()
  const { currentUser } = useAuthStore()
  const { showAlert } = useAlert()

  useEffect(() => {
    if (currentUser && !isStaff(currentUser) && fetchLoyaltyPointsForCustomer) {
      fetchLoyaltyPointsForCustomer(currentUser.id).then(() => console.info('OrderConfirmed.fetchLoyaltyPointsForCustomer finished'))
    }
  }, [currentUser, fetchLoyaltyPointsForCustomer])

  useEffect(() => {
    if (data?.loyaltyPointsAccrued) {
      showAlert(`You've earned ${data.loyaltyPointsAccrued} points!`, 'success', 6000)
    }
  }, [data])

  useEffect(() => {
    if (error) {
      const { message, suspended } = handleError(error)
      showAlert(message, 'error', 10000)
      if (suspended) {
        navigate('/suspended')
        return
      }
    }
  }, [error])

  const formattedPickup = data?.requestedPickupTime
    ? format(data.requestedPickupTime, 'do LLL yyyy HH:mm')
    : '';

  const axiosError = error ? handleError(error) : null


  return (
    <>
      <LoadingSpinner isLoading={loading}>
        <Stack sx={{ flex: 0, paddingX: '1rem', paddingTop: '1rem' }}>
          <Stack flexDirection="row" sx={{ alignItems: 'center' }}>
            <IconButton size='large' onClick={() => navigate('/', { replace: true })}><ChevronLeft /></IconButton>
            {
              data ? (
                <Typography
                  variant="h3"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.palette.common.white,
                  }}
                >
                  Order confirmed
                </Typography>
              ) : axiosError ? (
                <Typography variant="h3">There was an issue loading the order: {axiosError.message}</Typography>
              ) : (
                <Typography variant="h3">We cannot find the order you are looking for</Typography>

              )
            }
          </Stack>
          {data ? (
            <>
              <Divider sx={{ marginY: 1 }} />
              <Typography variant="h5">Pick up:</Typography>
              <Typography
                sx={{
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  display: 'flex',
                }}
              >
                <LocationOnOutlined sx={{ marginRight: '1rem' }} />
                {data.siteName}
              </Typography>
              <Typography
                sx={{
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  display: 'flex',
                }}
              >
                <ClockIcon sx={{ marginRight: '1rem' }} />
                {formattedPickup}
              </Typography>
              <OrderItemsList order={data} />
              <OrderPricingList order={data} />
            </>
          ) : null}
        </Stack>
      </LoadingSpinner>
    </>
  );
}
